import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import {
  CatalogLayout,
  CenteredLoadingSpinner,
  useFeature,
} from '@ole-ui/ole-ui-components';
import {
  Title,
  EmptyState,
  EmptyStateIcon,
  EmptyStateBody,
  Pagination,
  PaginationVariant,
} from '@patternfly/react-core';
import CatalogIcon from '@patternfly/react-icons/dist/esm/icons/catalog-icon';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import FlippingBanner from 'components/Banners/FlippingBanner';
import Filters from 'components/Catalog/Filters';
import { Modality, OfferingVisibility, QuotaSlug } from 'types';

import './styles.scss';
import useMobxStores from 'hooks/useMobxStores';
import ExamCard from 'components/Exam/Card';
import { EnablementFeatureFlags } from 'config/featureFlags';
import ProgressCatalogCard from './ProgressCatalogCard';
import FilterChips from './FilterChips';
import catalogPaginationPerPageOptions from './catalogPaginationOptions';

const NewCardGrid = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'catalog' */
      /* webpackPreload: true */
      './NewCardGrid'
    ),
);

const Catalog = () => {
  const { t } = useTranslation();
  const { catalogStore, routerStore, searchStore, userStore } = useMobxStores();

  const route = routerStore?.route as any;
  const pageTitle = route?.params?.title;

  const {
    currentPage,
    paginatedEntries,
    filteredCount,
    setCurrentPage,
    entriesPerPage,
    totalPages,
    loading,
  } = catalogStore;

  const newCardsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewCatalog,
    defaultFlagValue: false,
  });

  const applyPresetFiltersFromRoute = (
    params: Record<string, string> = {},
    applyMyOfferingsDefault = false,
  ) => {
    const shouldDefaultMyOfferings =
      applyMyOfferingsDefault &&
      !params.myOfferings &&
      (catalogStore.filters?.myOfferings?.length ?? 0) > 0;

    const merged = shouldDefaultMyOfferings
      ? { ...params, myOfferings: OfferingVisibility.Visible }
      : params;

    catalogStore.clearFilters(undefined, false);
    searchStore.query = merged?.q || '';
    catalogStore.presetFilters(merged);
    catalogStore.recalculateFilterCounts();

    if (shouldDefaultMyOfferings && routerStore?.route?.name) {
      routerStore.navigate(
        routerStore.route.name,
        {
          ...(routerStore.route as { params?: Record<string, string> }).params,
          myOfferings: OfferingVisibility.Visible,
        },
        // @ts-ignore // this is a bug in the types
        { replace: true },
      );
    }
  };

  useEffect(() => {
    // Populate entry data and filters on initial load
    when(
      () => catalogStore.loaded,
      async () => {
        if (userStore.hasActiveSubscription) {
          try {
            userStore.getQuotasUsage(QuotaSlug.ExamExperiences);
          } catch (e) {
            console.error(e);
          }
        }

        await catalogStore.getFilterInfo();
        // getFilterInfo() replaces filters (all unchecked); re-apply route params
        // so "Live classes" and other deep links stay in sync on second visit.
        if (routerStore?.route?.name === 'catalog') {
          const params =
            (routerStore.route as { params?: Record<string, string> })
              ?.params ?? {};
          applyPresetFiltersFromRoute(params, true);
        }
      },
    );
  }, []);

  // Sync filters from route whenever we're on catalog (MobX reaction so we always
  // read latest route; fixes "Live classes" link not syncing on second visit).
  useEffect(() => {
    const dispose = reaction(
      () => {
        const r = routerStore?.route as
          | { name?: string; params?: Record<string, string> }
          | undefined;
        if (r?.name !== 'catalog' || !catalogStore.filterInfoLoaded)
          return null;
        return JSON.stringify(r?.params ?? {});
      },
      (paramsKey) => {
        if (paramsKey === null) return;
        const params =
          (routerStore?.route as { params?: Record<string, string> })?.params ??
          {};
        catalogStore.setLastCatalogParams(params);
        applyPresetFiltersFromRoute(params);
      },
      { fireImmediately: true },
    );
    return () => dispose();
  }, []);

  const showEarlyAccessBanner =
    catalogStore?.filters?.earlyAccess?.length > 0 &&
    catalogStore?.filters?.earlyAccess[0]?.checked;

  const loadingElement = (
    <div key="catalog-loading" className="catalog__loading-container">
      <CenteredLoadingSpinner />
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return loadingElement;
    }

    if (!paginatedEntries.length) {
      return (
        <EmptyState>
          <EmptyStateIcon icon={CatalogIcon} />
          <Title size="lg" headingLevel="h4">
            {t('No catalog entries found')}
          </Title>
          <EmptyStateBody>
            {t(
              'No results match the filter criteria. Change the filters and try again.',
            )}
          </EmptyStateBody>
        </EmptyState>
      );
    }

    if (newCardsEnabled) {
      return (
        <React.Suspense fallback={loadingElement}>
          <div className="catalog__container">
            <CatalogLayout.Grid>
              <Pagination
                className="catalog__pagination catalog__pagination--top"
                itemCount={filteredCount}
                perPage={entriesPerPage}
                page={currentPage}
                onSetPage={(e, n) => setCurrentPage(n)}
                onPerPageSelect={(e, n) => {
                  catalogStore.entriesPerPage = n;
                }}
                perPageOptions={catalogPaginationPerPageOptions}
                widgetId="pagination-top"
                ouiaId="PaginationTop"
                aria-label={t('Top pagination controls')}
                variant={PaginationVariant.bottom}
              />
              <NewCardGrid />
              <Pagination
                className="catalog__pagination"
                itemCount={filteredCount}
                perPage={entriesPerPage}
                page={currentPage}
                onSetPage={(e, n) => setCurrentPage(n)}
                onPerPageSelect={(e, n) => {
                  catalogStore.entriesPerPage = n;
                }}
                perPageOptions={catalogPaginationPerPageOptions}
                widgetId="pagination-bottom"
                ouiaId="PaginationBottom"
                aria-label={t('Bottom pagination controls')}
                variant={PaginationVariant.bottom}
              />
            </CatalogLayout.Grid>
          </div>
        </React.Suspense>
      );
    }

    return (
      <>
        <div className="catalog__list">
          {paginatedEntries.map((entry) => {
            const { doc_id: docId } = entry;

            if (entry.modality === Modality.Exam) {
              return <ExamCard entry={entry} key={docId} />;
            }

            return <ProgressCatalogCard entry={entry} key={docId} />;
          })}
        </div>
        <UltimatePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
        />
      </>
    );
  };

  return (
    <>
      <Helmet title={`${pageTitle} - Red Hat Learning`} />
      <CatalogLayout>
        <CatalogLayout.Filters count={filteredCount}>
          {catalogStore.filterInfoLoaded && <Filters />}
        </CatalogLayout.Filters>
        <CatalogLayout.Content className="catalog__content">
          <div className="catalog__filter-chips-container">
            <FilterChips />
          </div>
          {showEarlyAccessBanner && <FlippingBanner banner="earlyAccess" />}
          {renderContent()}
        </CatalogLayout.Content>
      </CatalogLayout>
    </>
  );
};

export default withErrorBoundary(
  observer(Catalog),
  GenericViewError,
  handleError,
);
